/**
 * YouthHome.jsx — Duolingo-style scrollable path map for the Youth Learner
 * Winding nodes → chapter banners → animated trail → START badge
 * Unit Boss Challenge gates chapter reveals.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Heart, Zap, Flame, Lock, ChevronLeft, Check, Award,
  RotateCcw, Settings as SettingsIcon, Trophy, Star,
} from '../../../components/CustomIcons';
import { Mono } from '../primitives';
import { useYouth } from './youthState';
import {
  SOLAR_COURSE, ROBOT_COURSE, LOCKED_COURSES, COURSE_BY_ID,
  YOUTH_PATH, UNLOCK_RULES, BOSS_QUIZZES,
} from '../youthData';

/* ============================================================
   Youth Header
   ============================================================ */
export const YouthHeader = ({ title, subtitle, onBack, onRestart, onSettings, breadcrumbs, xp, hearts, showHearts, fxSettings, onToggleFx }) => (
  <header className="px-6 pt-4 pb-4 border-b border-[#e2e8f0] bg-[#fafaf9] sticky top-0 z-10">
    <div className="flex items-center gap-2">
      {onBack && (
        <button onClick={onBack} data-testid="youth-back"
          className="w-8 h-8 rounded-full flex items-center justify-center text-[#0f2744] hover:bg-[#f1f5f9] shrink-0"
          aria-label="Go back">
          <ChevronLeft className="w-5 h-5" aria-hidden="true" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="p-display text-[22px] leading-tight truncate" style={{ margin: 0 }}>{title}</h1>
        {subtitle && <p className="text-[13px] text-[#64748b] mt-0.5 truncate font-semibold uppercase tracking-wider">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {showHearts && (
          <div className="flex items-center gap-0.5" aria-label={`${hearts} hearts remaining`} data-testid="youth-hearts">
            {[0, 1, 2].map((i) => (
              <Heart key={i} className="w-4 h-4"
                fill={i < hearts ? '#f97316' : 'none'}
                stroke={i < hearts ? '#f97316' : '#cbd5e1'}
                strokeWidth={2} aria-hidden="true" />
            ))}
          </div>
        )}
        {onRestart && (
          <button onClick={onRestart} className="w-8 h-8 rounded-full flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9]"
            aria-label="Restart lesson" data-testid="youth-restart-lesson">
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        {onSettings && (
          <button onClick={onSettings} className="w-8 h-8 rounded-full flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9]"
            aria-label="Open settings" data-testid="youth-open-settings">
            <SettingsIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        {onToggleFx && (
          <button onClick={onToggleFx}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9]"
            aria-label={fxSettings?.sound || fxSettings?.haptic ? 'Sound on' : 'Sound off'}
            data-testid="youth-toggle-fx">
            <span className="text-[16px]" aria-hidden="true">{fxSettings?.sound || fxSettings?.haptic ? '🔔' : '🔕'}</span>
          </button>
        )}
        <div className="inline-flex items-center gap-1 rounded-full px-2 py-1"
          style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.30)' }}
          data-testid="youth-xp-counter">
          <Zap className="w-3.5 h-3.5" style={{ color: '#f97316' }} aria-hidden="true" />
          <span className="p-mono text-[12px] font-bold" style={{ color: '#c2410c' }}>{xp.toLocaleString()} XP</span>
        </div>
      </div>
    </div>
    {breadcrumbs && breadcrumbs.length > 0 && (
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        {breadcrumbs.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-[10px] text-[#94a3b8]" aria-hidden="true">›</span>}
            {b.onClick
              ? <button onClick={b.onClick} className="text-[10px] font-semibold uppercase tracking-widest text-[#64748b] hover:underline">{b.label}</button>
              : <span className="text-[10px] font-semibold uppercase tracking-widest text-[#0f2744]">{b.label}</span>}
          </React.Fragment>
        ))}
      </div>
    )}
  </header>
);

/* ============================================================
   Duolingo-style Path Map components
   ============================================================ */

/* Chapter section banner */
const ChapterBanner = ({ number, title, emoji, subtitle, allDone }) => (
  <div className="my-4 rounded-[12px] overflow-hidden shadow-sm" data-testid={`youth-chapter-${number}`}>
    <div className="flex items-center gap-3 px-4 py-3"
      style={{ background: allDone ? 'linear-gradient(135deg,#15803d 0%,#166534 100%)' : 'linear-gradient(135deg,#0f2744 0%,#1e3a5f 100%)' }}>
      <div className="w-10 h-10 rounded-[10px] flex items-center justify-center text-[22px] shrink-0"
        style={{ background: 'rgba(255,255,255,0.12)' }} aria-hidden="true">
        {allDone ? '🏆' : emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'rgba(250,250,249,0.65)' }}>
          Unit {number}
        </div>
        <div className="p-display text-[15px] leading-tight" style={{ color: '#fafaf9', margin: 0 }}>
          {allDone ? `${title} — Complete!` : title}
        </div>
        {subtitle && <div className="text-[11px] mt-0.5" style={{ color: 'rgba(250,250,249,0.65)' }}>{subtitle}</div>}
      </div>
      {allDone && <Trophy className="w-5 h-5 shrink-0" style={{ color: '#fbbf24' }} aria-hidden="true" />}
    </div>
  </div>
);

/* Individual path node */
const PathNode = ({ status, title, emoji, xpReward, onClick, index, testId, isFirst }) => {
  const isLocked = status === 'locked';
  const isDone = status === 'done';
  const isOpen = status === 'open';

  const OFFSETS = [0, 48, 16, -36, -8, 40];
  const offset = OFFSETS[index % OFFSETS.length];

  const bg = isDone ? '#15803d' : isOpen ? '#0f2744' : '#e2e8f0';
  const fgColor = isLocked ? '#94a3b8' : '#fafaf9';

  return (
    <div className="relative flex flex-col items-center" style={{ marginLeft: offset, transition: 'margin 300ms ease' }}>
      {isOpen && (
        <div aria-hidden="true" className="absolute inset-0 rounded-full pointer-events-none"
          style={{ animation: 'youth-node-pulse 1800ms ease-in-out infinite', boxShadow: '0 8px 28px -4px rgba(249,115,22,0.55)' }} />
      )}
      {isOpen && isFirst && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex flex-col items-center" aria-hidden="true">
          <span className="text-[18px]" style={{ animation: 'youth-bounce 1000ms ease-in-out infinite' }}>🧑‍🚀</span>
          <div className="w-0.5 h-3 mt-0.5 rounded-full" style={{ background: '#f97316' }} />
        </div>
      )}
      <button onClick={onClick} data-testid={testId}
        aria-label={`${title} — ${status}`}
        className="relative rounded-full flex items-center justify-center transition-all duration-200"
        style={{
          width: 78, height: 78, background: bg, color: fgColor,
          border: isOpen ? '3px solid #f97316' : isDone ? '3px solid #15803d' : '3px solid #d1d5db',
          boxShadow: isDone ? '0 6px 20px -4px rgba(21,128,61,0.45)' : isOpen ? '0 6px 20px -4px rgba(249,115,22,0.4)' : 'none',
          cursor: isLocked ? 'default' : 'pointer',
        }}>
        {isLocked
          ? <Lock className="w-7 h-7" aria-hidden="true" />
          : isDone
            ? <Check className="w-8 h-8" strokeWidth={2.5} aria-hidden="true" />
            : <span className="text-[30px]" aria-hidden="true">{emoji}</span>}
        {isOpen && (
          <span className="absolute -bottom-2.5 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest"
            style={{ background: '#f97316', color: '#0f2744', letterSpacing: '0.1em' }} aria-hidden="true">
            START
          </span>
        )}
        {isDone && xpReward && (
          <span className="absolute -top-2 -right-2 rounded-full px-1.5 py-0.5 text-[9px] font-black"
            style={{ background: '#fbbf24', color: '#0f2744' }} aria-hidden="true">
            ✓ {xpReward}
          </span>
        )}
      </button>
      <div className="mt-4 text-center" style={{ maxWidth: 110 }}>
        <div className="text-[13px] font-semibold leading-tight" style={{ color: isLocked ? '#94a3b8' : '#0f2744' }}>{title}</div>
        {isLocked && <div className="text-[10px] mt-0.5 font-semibold uppercase tracking-wider" style={{ color: '#cbd5e1' }}>Locked</div>}
      </div>
    </div>
  );
};

/* Trail segment between two nodes */
const TrailSegment = ({ done }) => (
  <div className="w-full flex justify-center" style={{ height: 36, margin: '2px 0' }} aria-hidden="true">
    <div style={{
      width: 4, height: '100%',
      background: done ? '#15803d' : 'transparent',
      borderLeft: done ? 'none' : '3px dashed #d1d5db',
      borderRadius: 4,
      transition: 'background 600ms ease-out',
    }} />
  </div>
);

/* ============================================================
   Level-Up Gate — appears between chapters after boss is beaten
   ============================================================ */
const ChapterGate = ({ chapterTitle, xpEarned, nextChapterTitle }) => (
  <div className="flex flex-col items-center py-1 px-4" data-testid="youth-level-gate">
    <div aria-hidden="true" style={{ width: 4, height: 18, background: '#15803d', borderRadius: 4 }} />
    <div className="relative rounded-[12px] px-5 py-3 flex items-center gap-3 w-full"
      style={{
        background: 'linear-gradient(135deg,#fbbf24 0%,#f59e0b 100%)',
        border: '2.5px solid #d97706',
        boxShadow: '0 8px 28px -6px rgba(245,158,11,0.60)',
        animation: 'youth-gate-pop 400ms cubic-bezier(0.34,1.56,0.64,1) both',
      }}>
      <div className="relative w-12 h-12 shrink-0 flex items-center justify-center rounded-full"
        style={{ background: 'rgba(255,255,255,0.28)' }}>
        <Trophy size={22} style={{ color: '#0f2744' }} aria-hidden="true" />
        <div aria-hidden="true" className="absolute inset-0 rounded-full"
          style={{ animation: 'youth-spin 8s linear infinite', border: '2px dashed rgba(15,39,68,0.25)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-black leading-tight" style={{ color: '#0f2744' }}>
          {chapterTitle} Complete!
        </div>
        <div className="p-mono text-[11px] mt-0.5 font-bold" style={{ color: '#451a03' }}>
          +{xpEarned.toLocaleString()} XP earned
        </div>
      </div>
      <div className="absolute -top-3 right-4 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest"
        style={{ background: '#0f2744', color: '#fbbf24', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
        aria-label="Level up">
        ✦ Level Up!
      </div>
    </div>
    <div className="flex flex-col items-center mt-2 gap-0.5">
      <div style={{ width: 4, height: 14, background: '#15803d', borderRadius: 4 }} aria-hidden="true" />
      <div className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest"
        style={{ background: 'rgba(21,128,61,0.15)', color: '#15803d', border: '1px solid rgba(21,128,61,0.35)' }}>
        {nextChapterTitle} Unlocked ▼
      </div>
      <div style={{ width: 4, height: 12, background: '#d1d5db', borderRadius: 4 }} aria-hidden="true" />
    </div>
  </div>
);

/* ============================================================
   Boss Node — clickable gate shown when prev chapter is done
   but boss quiz not yet passed
   ============================================================ */
const BossNode = ({ quizKey, onStartQuiz }) => {
  const meta = BOSS_QUIZZES[quizKey];
  return (
    <div className="flex flex-col items-center py-2 px-4" data-testid={`youth-boss-node-${quizKey}`}>
      <div aria-hidden="true" style={{ width: 4, height: 18, background: '#15803d', borderRadius: 4 }} />
      <button
        onClick={onStartQuiz}
        aria-label={`Start ${meta?.title || 'Boss Challenge'}`}
        className="relative rounded-[12px] px-5 py-3 flex items-center gap-3 w-full"
        style={{
          background: 'linear-gradient(135deg,#7c3aed 0%,#4f46e5 100%)',
          border: '2.5px solid #5b21b6',
          boxShadow: '0 8px 28px -6px rgba(109,40,217,0.50)',
          animation: 'youth-gate-pop 400ms cubic-bezier(0.34,1.56,0.64,1) both',
          cursor: 'pointer',
        }}>
        <div className="relative w-12 h-12 shrink-0 flex items-center justify-center rounded-full"
          style={{ background: 'rgba(255,255,255,0.15)' }}>
          <span className="text-[24px]" aria-hidden="true">{meta?.emoji || '⚡'}</span>
          <div aria-hidden="true" className="absolute inset-0 rounded-full"
            style={{ animation: 'youth-spin 8s linear infinite', border: '2px dashed rgba(255,255,255,0.25)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-black leading-tight" style={{ color: '#fff' }}>Boss Challenge!</div>
          <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.75)' }}>Answer 3 questions to level up</div>
        </div>
        <div className="absolute -top-3 right-4 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest"
          style={{ background: '#fbbf24', color: '#1e1b4b', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
          3 Q's
        </div>
      </button>
      <div aria-hidden="true" style={{ width: 4, height: 14, background: '#d1d5db', borderRadius: 4 }} />
    </div>
  );
};

/* ============================================================
   Boss Quiz Overlay — full-screen quiz modal inside tablet
   ============================================================ */
const BossQuizOverlay = ({ quiz, onPass, onClose }) => {
  const [phase, setPhase] = useState('quiz'); // 'quiz' | 'pass' | 'fail'
  const [qIdx, setQIdx]   = useState(0);
  const [picked, setPicked] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);

  const q = quiz.questions[qIdx];
  const totalQ = quiz.questions.length;

  const choose = (opt) => {
    if (showFeedback) return;
    setPicked(opt);
    setShowFeedback(true);
  };

  const advance = () => {
    const newScore = score + (picked.c ? 1 : 0);
    if (qIdx < totalQ - 1) {
      setScore(newScore);
      setQIdx((i) => i + 1);
      setPicked(null);
      setShowFeedback(false);
    } else {
      setScore(newScore);
      setPhase(newScore === totalQ ? 'pass' : 'fail');
    }
  };

  const retry = () => {
    setPhase('quiz'); setQIdx(0);
    setPicked(null); setShowFeedback(false); setScore(0);
  };

  /* ── Pass screen ── */
  if (phase === 'pass') {
    return (
      <div className="absolute inset-0 z-[200] flex flex-col" style={{ background: 'linear-gradient(160deg,#15803d 0%,#166534 100%)' }}>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="text-[64px]" aria-hidden="true">🏆</div>
          <h2 className="p-display text-[22px] mt-3 leading-tight" style={{ color: '#fafaf9', margin: '12px 0 0' }}>Boss Defeated!</h2>
          <p className="text-[14px] mt-3" style={{ color: 'rgba(250,250,249,0.80)', maxWidth: '28ch' }}>
            You got {totalQ}/{totalQ} correct — Unit unlocked!
          </p>
          <button
            onClick={onPass}
            data-testid="youth-boss-pass-btn"
            className="mt-6 rounded-full px-7 py-3.5 text-[15px] font-black"
            style={{ background: '#fbbf24', color: '#0f2744', boxShadow: '0 8px 24px -4px rgba(251,191,36,0.55)' }}>
            Unlock Level Up ✦
          </button>
        </div>
      </div>
    );
  }

  /* ── Fail screen ── */
  if (phase === 'fail') {
    return (
      <div className="absolute inset-0 z-[200] flex flex-col" style={{ background: '#0f2744' }}>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="text-[64px]" aria-hidden="true">💪</div>
          <h2 className="p-display text-[22px] mt-3 leading-tight" style={{ color: '#fafaf9', margin: '12px 0 0' }}>Keep Practicing!</h2>
          <p className="text-[14px] mt-3" style={{ color: 'rgba(250,250,249,0.75)', maxWidth: '30ch' }}>
            You got {score}/{totalQ} correct. You need all {totalQ}/3 to pass. Review the lessons and try again!
          </p>
          <div className="flex gap-3 mt-7">
            <button
              onClick={onClose}
              className="rounded-full px-5 py-2.5 text-[13px] font-bold"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#fafaf9' }}>
              Back
            </button>
            <button
              onClick={retry}
              data-testid="youth-boss-retry-btn"
              className="rounded-full px-6 py-2.5 text-[14px] font-black"
              style={{ background: '#f97316', color: '#fff' }}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Quiz phase ── */
  return (
    <div className="absolute inset-0 z-[200] flex flex-col bg-[#fafaf9]" data-testid="youth-boss-quiz">
      {/* Quiz header */}
      <div className="px-6 pt-4 pb-3 border-b border-[#e2e8f0]"
        style={{ background: 'linear-gradient(135deg,#7c3aed 0%,#4f46e5 100%)' }}>
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            aria-label="Close quiz">
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {quiz.title}
            </div>
            <div className="p-mono text-[13px] font-black text-white">{qIdx + 1} / {totalQ}</div>
          </div>
          <div className="w-8" />
        </div>
        <div className="mt-3 w-full h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.20)' }}>
          <div className="h-full rounded-full transition-all duration-400"
            style={{ width: `${(qIdx / totalQ) * 100}%`, background: '#fbbf24' }} />
        </div>
      </div>

      {/* Question body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <p className="p-display text-[18px] leading-snug text-[#0f2744]" style={{ margin: '0 0 20px' }}>
          {q.q}
        </p>
        <div className="space-y-3" role="radiogroup" aria-label="Answer options">
          {q.options.map((opt, i) => {
            const isSel = picked === opt;
            let bg = '#fff', border = '#e2e8f0', textColor = '#0f2744';
            if (showFeedback) {
              if (opt.c)            { bg = 'rgba(21,128,61,0.10)'; border = '#15803d'; textColor = '#166534'; }
              else if (isSel)       { bg = 'rgba(220,38,38,0.08)'; border = '#dc2626'; textColor = '#b91c1c'; }
            } else if (isSel)       { bg = 'rgba(124,58,237,0.10)'; border = '#7c3aed'; }
            return (
              <button
                key={i}
                onClick={() => choose(opt)}
                disabled={showFeedback}
                data-testid={`youth-boss-opt-${i}`}
                className="w-full rounded-[12px] px-4 py-3 text-left text-[14px] font-semibold transition-all duration-150"
                style={{ background: bg, border: `2px solid ${border}`, color: textColor, cursor: showFeedback ? 'default' : 'pointer' }}
                aria-pressed={isSel}>
                {opt.t}
                {showFeedback && opt.c  && <span className="ml-2 text-[#15803d]" aria-hidden="true">✓</span>}
                {showFeedback && isSel && !opt.c && <span className="ml-2 text-[#dc2626]" aria-hidden="true">✗</span>}
              </button>
            );
          })}
        </div>

        {showFeedback && (
          <div className="mt-4 rounded-[12px] p-3"
            style={{ background: picked.c ? 'rgba(21,128,61,0.07)' : 'rgba(220,38,38,0.06)', border: `1.5px solid ${picked.c ? '#15803d' : '#dc2626'}` }}>
            <p className="text-[13px] font-bold" style={{ color: picked.c ? '#166534' : '#b91c1c' }}>
              {picked.c ? '✓ Correct!' : '✗ Not quite.'}
            </p>
            {q.explanation && <p className="text-[12px] mt-1 text-[#0f2744]">{q.explanation}</p>}
          </div>
        )}
      </div>

      {/* Next button */}
      {showFeedback && (
        <div className="px-6 pb-5 pt-3 border-t border-[#e2e8f0]">
          <button
            onClick={advance}
            data-testid="youth-boss-next"
            className="w-full rounded-full py-3 text-[15px] font-black"
            style={{ background: '#0f2744', color: '#fafaf9' }}>
            {qIdx < totalQ - 1 ? 'Next Question →' : 'See Results'}
          </button>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   Confetti burst — 24 CSS-animated particles
   ============================================================ */
const CONF_COLORS = ['#f97316','#fbbf24','#0f2744','#15803d','#fb923c','#fde68a','#166534','#c2410c'];
const CONF_PARTS  = Array.from({ length: 24 }, (_, i) => ({
  angle: (i / 24) * 360,
  dist:  55 + (i % 3) * 22,
  delay: (i % 5) * 60,
  size:  5 + (i % 3) * 3,
  color: CONF_COLORS[i % CONF_COLORS.length],
  shape: i % 3 === 0 ? '50%' : (i % 3 === 1 ? '2px' : '0'),
}));

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[16px]"
    aria-hidden="true" style={{ zIndex: 20 }}>
    {CONF_PARTS.map((p, i) => (
      <div key={i} style={{
        position: 'absolute', top: '50%', left: '50%',
        width: p.size, height: p.size,
        background: p.color, borderRadius: p.shape,
        animation: `youth-conf-${i % 4} 900ms ${p.delay}ms cubic-bezier(0,0.9,0.57,1) both`,
        '--dx': `${Math.round(Math.cos((p.angle * Math.PI) / 180) * p.dist)}px`,
        '--dy': `${Math.round(Math.sin((p.angle * Math.PI) / 180) * p.dist)}px`,
      }} />
    ))}
    <style>{`
      @keyframes youth-conf-0 { to { transform: translate(var(--dx),var(--dy)) rotate(360deg); opacity:0; } }
      @keyframes youth-conf-1 { to { transform: translate(var(--dx),var(--dy)) rotate(-270deg) scale(.4); opacity:0; } }
      @keyframes youth-conf-2 { to { transform: translate(var(--dx),var(--dy)) rotate(540deg) scale(.3); opacity:0; } }
      @keyframes youth-conf-3 { to { transform: translate(var(--dx),var(--dy)) rotate(-180deg) scaleY(.2); opacity:0; } }
    `}</style>
  </div>
);

/* ============================================================
   Chapter chapters config */
const CHAPTERS = [
  {
    id: 'ch1',
    number: 1,
    title: 'Starter Worlds',
    emoji: '🌍',
    subtitle: 'Solar power & robotics — pick your start!',
    nodeIds: ['solar', 'robot'],
  },
  {
    id: 'ch2',
    number: 2,
    title: 'Level Up',
    emoji: '🚀',
    subtitle: 'Manufacturing & cyber defense',
    nodeIds: ['maker', 'cyber'],
  },
  {
    id: 'ch3',
    number: 3,
    title: 'Expert Tier',
    emoji: '⚡',
    subtitle: 'Electrical & coding mastery',
    nodeIds: ['spark', 'code'],
  },
];

/* ============================================================
   YouthHome — main exported home screen
   ============================================================ */
export const YouthHome = ({ state, update, goToCourse, goToLocked, goToBadges, goToSettings }) => {
  const { fxSettings } = useYouth();
  const [activeBossQuiz, setActiveBossQuiz] = useState(null);

  const dailyProgress = Math.min(3, state.dailyDone || 0);
  const dailyPct = (dailyProgress / 3) * 100;
  const completed = state.completedCourses || {};
  const badges = state.earnedBadges || {};
  const bossResults = state.bossResults || {};

  const allCourses = [
    SOLAR_COURSE, ROBOT_COURSE,
    ...LOCKED_COURSES,
  ].reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  const nodeStatus = (id, kind) => {
    if (completed[id]) return 'done';
    if (kind === 'open') return 'open';
    const rule = UNLOCK_RULES[id];
    return rule && rule(completed) ? 'open' : 'locked';
  };

  const nodes = YOUTH_PATH.map((n, i) => ({
    ...n,
    status: nodeStatus(n.id, n.kind),
    course: allCourses[n.id],
    globalIndex: i,
  }));

  const completedCount = Object.keys(completed).length;
  const totalXP = state.xp || 0;
  const firstOpenIdx = nodes.findIndex((n) => n.status === 'open');

  /* ── Chapter reveal tracking ── */
  const SEEN_KEY = 'assistivos_youth_chapters_seen';
  const [seenChapters, setSeenChapters] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
    catch (_) { return new Set(); }
  });
  const [showConfetti, setShowConfetti] = useState(new Set());
  const confettiTimers = useRef({});

  useEffect(() => {
    const newlySeen = new Set(seenChapters);
    const newConfetti = new Set(showConfetti);
    let changed = false;

    CHAPTERS.forEach((ch, idx) => {
      if (idx === 0) return;
      const bossKey = `ch${idx}-ch${idx + 1}`;
      const prevChapter  = CHAPTERS[idx - 1];
      const prevNodes    = nodes.filter((n) => prevChapter.nodeIds.includes(n.id));
      const prevAllDone  = prevNodes.every((n) => n.status === 'done');
      const bossPassed   = !!bossResults[bossKey];
      // Chapter reveals only when prev done AND boss beaten
      if (prevAllDone && bossPassed && !newlySeen.has(ch.id)) {
        newlySeen.add(ch.id);
        newConfetti.add(ch.id);
        changed = true;
        if (confettiTimers.current[ch.id]) clearTimeout(confettiTimers.current[ch.id]);
        confettiTimers.current[ch.id] = setTimeout(() => {
          setShowConfetti((prev) => { const s = new Set(prev); s.delete(ch.id); return s; });
        }, 1400);
      }
    });

    if (changed) {
      setSeenChapters(newlySeen);
      setShowConfetti(newConfetti);
      try { localStorage.setItem(SEEN_KEY, JSON.stringify([...newlySeen])); } catch (_) {}
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timers = confettiTimers.current;
      Object.values(timers).forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(completed), JSON.stringify(bossResults)]);

  return (
    <>
      <YouthHeader
        title="Welcome back, Jordan!"
        subtitle="Youth Learning Program"
        xp={state.xp}
        hearts={state.hearts}
        onSettings={goToSettings}
      />

      <main className="overflow-y-auto flex-1" data-testid="youth-home">
        {/* ── Streak Card ── */}
        <div className="px-6 pt-5 pb-3">
          <section className="rounded-[12px] p-5"
            style={{ background: 'linear-gradient(135deg,#0f2744 0%,#1e3a5f 100%)' }}
            aria-label="Streak status">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5" style={{ color: '#f97316' }} aria-hidden="true" />
              <div className="p-display text-[19px] leading-tight" style={{ color: '#fafaf9', margin: 0 }}>
                <Mono>{state.streakDays}</Mono>-day streak!
              </div>
            </div>
            <p className="text-[13px] mt-1" style={{ color: 'rgba(250,250,249,0.7)' }}>Keep it going — learn something today</p>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] font-semibold mb-1"
                style={{ color: 'rgba(250,250,249,0.85)' }}>
                <span>Today's goal: 3 lessons</span>
                <span className="p-mono">{dailyProgress}/3</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.15)' }}
                role="progressbar" aria-valuenow={dailyProgress} aria-valuemax={3}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${dailyPct}%`, background: '#f97316' }} />
              </div>
            </div>
          </section>
        </div>

        {/* ── Stats row ── */}
        <div className="px-6 py-2">
          <section aria-label="Path progress" className="grid grid-cols-3 gap-2" data-testid="youth-path-progress">
            <div className="rounded-[12px] p-3 text-center" style={{ background: '#f1f5f9' }}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Progress</div>
              <div className="p-mono text-[18px] mt-0.5 text-[#0f2744]">
                {completedCount}<span className="text-[12px] text-[#94a3b8]">/{YOUTH_PATH.length}</span>
              </div>
            </div>
            <div className="rounded-[12px] p-3 text-center" style={{ background: 'rgba(21,128,61,0.10)' }}>
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#15803d' }}>Badges</div>
              <div className="p-mono text-[18px] mt-0.5" style={{ color: '#166534' }}>
                {Object.keys(badges).length}<span className="text-[12px]" style={{ color: 'rgba(21,128,61,0.5)' }}>/{YOUTH_PATH.length}</span>
              </div>
            </div>
            <div className="rounded-[12px] p-3 text-center" style={{ background: 'rgba(249,115,22,0.10)' }}>
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#c2410c' }}>Total XP</div>
              <div className="p-mono text-[18px] mt-0.5" style={{ color: '#c2410c' }}>{totalXP.toLocaleString()}</div>
            </div>
          </section>
        </div>

        {/* ── Duolingo Path Map ── */}
        <div className="px-4 pb-6" data-testid="youth-path-map">
          <div className="flex items-center justify-between px-1 mb-1">
            <h2 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest">Learning Path</h2>
            <button onClick={goToBadges} data-testid="youth-view-badges"
              className="text-[12px] font-semibold" style={{ color: '#f97316' }}>
              Badges →
            </button>
          </div>

          <div className="rounded-[12px] overflow-hidden" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            {CHAPTERS.map((chapter, chIdx) => {
              const chapterNodes   = nodes.filter((n) => chapter.nodeIds.includes(n.id));
              const allChapterDone = chapterNodes.every((n) => n.status === 'done');
              const prevChapter    = chIdx > 0 ? CHAPTERS[chIdx - 1] : null;
              const prevNodes      = prevChapter ? nodes.filter((n) => prevChapter.nodeIds.includes(n.id)) : [];
              const prevAllDone    = prevNodes.every((n) => n.status === 'done');

              const bossKey   = chIdx > 0 ? `ch${chIdx}-ch${chIdx + 1}` : null;
              const bossPassed = !bossKey || !!bossResults[bossKey];

              /* Chapter is "revealed" if it's ch1, or prev done + boss beaten */
              const isRevealed  = chIdx === 0 || (prevAllDone && bossPassed);
              const isNewReveal = showConfetti.has(chapter.id);

              /* XP totals for gate */
              const prevChEarned = prevChapter
                ? prevNodes.filter((n) => n.status === 'done')
                    .reduce((s, n) => s + (n.course?.xp || n.course?.xpAvailable || 150), 0)
                : 0;

              return (
                <div key={chapter.id}>
                  {/* ── Boss Challenge Node (prev done, boss not yet beaten) ── */}
                  {chIdx > 0 && prevAllDone && !bossPassed && (
                    <BossNode
                      quizKey={bossKey}
                      onStartQuiz={() => setActiveBossQuiz(bossKey)}
                    />
                  )}

                  {/* ── Level-Up Gate (prev done + boss beaten) ── */}
                  {chIdx > 0 && prevAllDone && bossPassed && (
                    <ChapterGate
                      chapterTitle={prevChapter.title}
                      xpEarned={prevChEarned}
                      nextChapterTitle={chapter.title}
                    />
                  )}

                  {/* ── Locked future chapter teaser (prev not yet done) ── */}
                  {chIdx > 0 && !prevAllDone && (
                    <div className="mx-4 my-3 rounded-[14px] px-4 py-3 flex items-center gap-3"
                      style={{ background: 'rgba(100,116,139,0.07)', border: '1.5px dashed #cbd5e1' }}
                      data-testid={`youth-chapter-locked-${chapter.id}`}>
                      <div className="w-10 h-10 rounded-[10px] flex items-center justify-center text-[22px]"
                        style={{ filter: 'grayscale(1) opacity(0.35)' }} aria-hidden="true">
                        {chapter.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#94a3b8' }}>
                          Unit {chapter.number}
                        </div>
                        <div className="text-[14px] font-black leading-tight" style={{ color: '#94a3b8' }}>
                          {chapter.title}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: '#cbd5e1' }}>
                          Complete Unit {chapter.number - 1} to unlock
                        </div>
                      </div>
                      <Lock size={18} style={{ color: '#d1d5db' }} aria-hidden="true" />
                    </div>
                  )}

                  {/* ── Full chapter content (visible once revealed) ── */}
                  {isRevealed && (
                  <div style={isNewReveal ? { animation: 'youth-chapter-reveal 500ms 80ms cubic-bezier(0.22,1,0.36,1) both' } : undefined}>
                  {/* Confetti burst on first reveal — respects confetti setting */}
                  {isNewReveal && fxSettings?.confetti !== false && (
                    <div className="relative" aria-hidden="true">
                      <Confetti />
                    </div>
                  )}

                  {/* ── Chapter Banner ── */}
                  <ChapterBanner
                    number={chapter.number}
                    title={chapter.title}
                    emoji={chapter.emoji}
                    subtitle={chapter.subtitle}
                    allDone={allChapterDone}
                  />

                  {/* ── Chapter XP progress ── */}
                  {(() => {
                    const chDone = chapterNodes.filter((n) => n.status === 'done').length;
                    const chEarned = chapterNodes
                      .filter((n) => n.status === 'done')
                      .reduce((s, n) => s + (n.course?.xp || n.course?.xpAvailable || 150), 0);
                    const chTotal = chapterNodes
                      .reduce((s, n) => s + (n.course?.xp || n.course?.xpAvailable || 150), 0);
                    const pct = chTotal ? (chEarned / chTotal) * 100 : 0;
                    return (
                      <div className="mb-3 -mt-1 rounded-[12px] px-3 pt-2 pb-2.5"
                        style={{ background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.14)' }}
                        data-testid={`youth-chapter-xp-${chapter.id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>
                            {chDone}/{chapterNodes.length} courses
                          </span>
                          <span className="p-mono text-[11px] font-bold"
                            style={{ color: chEarned > 0 ? '#c2410c' : '#94a3b8' }}>
                            {chEarned > 0
                              ? `${chEarned.toLocaleString()} XP earned`
                              : `${chTotal.toLocaleString()} XP available`}
                          </span>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}
                          role="progressbar" aria-valuenow={chDone} aria-valuemax={chapterNodes.length}>
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#f97316,#fb923c)' }} />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Nodes + trails */}
                  <div className="px-4 pb-4 flex flex-col items-center">
                    {chapterNodes.map((n, localIdx) => {
                      const globalIdx = nodes.indexOf(n);
                      const isLastInChapter = localIdx === chapterNodes.length - 1;
                      const nextNode = nodes[globalIdx + 1];
                      const trailActive = n.status === 'done';
                      const isFirstOpen = globalIdx === firstOpenIdx;

                      return (
                        <React.Fragment key={n.id}>
                          <PathNode
                            index={globalIdx}
                            status={n.status}
                            emoji={n.course?.emoji}
                            title={n.course?.title}
                            xpReward={n.status === 'done' ? `+${(n.course?.xp || n.course?.xpAvailable || 150)}XP` : null}
                            testId={`youth-node-${n.id}`}
                            isFirst={isFirstOpen}
                            onClick={() => {
                              if (n.status === 'locked') { goToLocked(n.id); return; }
                              if (COURSE_BY_ID[n.id]) goToCourse(n.id);
                              else goToLocked(n.id);
                            }}
                          />
                          {!isLastInChapter && nextNode && (
                            <TrailSegment done={trailActive} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  </div>
                  )} {/* /isRevealed */}
                </div>
              );
            })}

            {/* Final completion state */}
            {completedCount === YOUTH_PATH.length && (
              <div className="mx-4 mb-4 rounded-[14px] p-4 text-center"
                style={{ background: 'linear-gradient(135deg,#15803d 0%,#166534 100%)' }}>
                <div className="text-[36px]" aria-hidden="true">🏆</div>
                <div className="p-display text-[16px] mt-2" style={{ color: '#fafaf9', margin: '8px 0 0' }}>
                  All Courses Complete!
                </div>
                <div className="p-mono text-[13px] mt-1" style={{ color: 'rgba(250,250,249,0.75)' }}>
                  {totalXP.toLocaleString()} Total XP Earned
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Recent Badges ── */}
        <div className="px-6 pb-6">
          <section aria-label="Recent badges">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest">Your Badges</h2>
              <button onClick={goToBadges} data-testid="youth-view-all-badges"
                className="text-[12px] font-semibold" style={{ color: '#f97316' }}>View all →</button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[SOLAR_COURSE, ROBOT_COURSE, LOCKED_COURSES[0]].map((c) => {
                const earned = badges[c.id];
                return (
                  <div key={c.id} className="rounded-[12px] p-3 text-center"
                    style={{ background: earned ? 'rgba(249,115,22,0.10)' : '#f1f5f9', border: earned ? '1px solid rgba(249,115,22,0.35)' : '1px solid transparent' }}>
                    <div className="text-[30px]" style={{ filter: earned ? 'none' : 'grayscale(1) opacity(0.4)' }} aria-hidden="true">
                      {c.badge?.icon || c.emoji}
                    </div>
                    <div className="text-[10px] font-bold mt-1 leading-tight" style={{ color: earned ? '#c2410c' : '#94a3b8' }}>
                      {earned ? c.badge?.name || 'Earned' : 'Locked'}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>

      {/* ── Boss Quiz Overlay ── */}
      {activeBossQuiz && (
        <BossQuizOverlay
          quiz={BOSS_QUIZZES[activeBossQuiz]}
          onPass={() => {
            update((s) => ({
              ...s,
              bossResults: { ...(s.bossResults || {}), [activeBossQuiz]: true },
            }));
            setActiveBossQuiz(null);
          }}
          onClose={() => setActiveBossQuiz(null)}
        />
      )}

      {/* All animation keyframes */}
      <style>{`
        @keyframes youth-bounce {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes youth-gate-pop {
          from { transform: scale(0.72) translateY(12px); opacity: 0; }
          to   { transform: scale(1) translateY(0);      opacity: 1; }
        }
        @keyframes youth-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes youth-chapter-reveal {
          from { transform: translateY(28px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
};
